import { Component, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Chart, registerables } from "chart.js";
import {
  MetasPlanCompetenciaService,
  MetasPlanCompetencia,
} from "../../../../../shared/services/metas-plan-competencia.service";
import { Utils } from "../../../../../shared/utils/Utils";
import {
  PlanCompetenciaAnualService,
  PlanCompetenciaAnual,
  PlanCompetenciaAnualPayload,
  CronogramaAnualResponse,
} from "../../../../../shared/services/plan-competencia-anual.service";
import { AdministracionService } from "../../../../../shared/services/administracion.services";
import { PropietarioService } from "../../../../../shared/services/propietarios.service"; // 👈 NUEVO

Chart.register(...registerables);

interface CoberturaPersonal {
  capacitados: number;
  totalColaboradores: number;
  porcentaje: number;
}

interface CoberturaPorCargo {
  cargo: string;
  totalColaboradores: number;
  capacitados: number;
  porcentaje: number;
}

@Component({
  selector: "app-plan-competencia",
  templateUrl: "./plan-competencia.component.html",
  styleUrls: ["./plan-competencia.component.scss"],
})
export class PlanCompetenciaComponent implements OnInit {
  planForm!: FormGroup;

  planes: PlanCompetenciaAnual[] = [];
  planesFiltrados: PlanCompetenciaAnual[] = [];

  filtro = "";
  filtroEstado: number | null = null;
  pageSize = 10;
  page = 1;

  cargandoListado = false;
  guardando = false;
  generandoExcel = false;

  modoEdicion = false;
  idEditando: number | null = null;
  anios: number[] = [];
  metasIds: Array<number | null> = [];

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private planCompetenciaAnualService: PlanCompetenciaAnualService,
    private administracionService: AdministracionService,
    private metasPlanCompetenciaService: MetasPlanCompetenciaService,
    private propietarioService: PropietarioService, // 👈 NUEVO
  ) {}

  ngOnInit(): void {
    this.generarAnios();
    this.inicializarFormulario();
    this.cargarListado();
  }

  // ===================== RESUMEN (tarjetas superiores) =====================

  get totalPlanes(): number {
    return this.planes.length;
  }

  get totalActivos(): number {
    return this.planes.filter((p) => Number(p.estado) === 1).length;
  }

  get planVigenteAnioActual(): PlanCompetenciaAnual | undefined {
    const anioActual = new Date().getFullYear();
    return this.planes.find(
      (p) => Number(p.estado) === 1 && this.obtenerAnioDeFecha(p.fecha_inicio) === anioActual,
    );
  }

  private generarAnios(): void {
    const anioActual = new Date().getFullYear();
    const desde = anioActual - 5;

    this.anios = [];
    for (let a = anioActual; a >= desde; a--) {
      this.anios.push(a);
    }
  }

  private inicializarFormulario(): void {
    this.planForm = this.fb.group({
      objetivo: ["", [Validators.required, Validators.maxLength(500)]],
      anio: [null, Validators.required],
      fecha_inicio: [{ value: null, disabled: true }],
      fecha_fin: [{ value: null, disabled: true }],
      metas: this.fb.array([], Validators.required),
    });

    this.planForm.get("anio")?.valueChanges.subscribe((anio: number | null) => {
      this.recalcularFechasPorAnio(anio);
    });
  }

  get metasArray(): FormArray {
    return this.planForm.get("metas") as FormArray;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByPlanId(_index: number, plan: PlanCompetenciaAnual): number | undefined {
    return plan.id;
  }

  agregarMeta(valor: string = "", id: number | null = null): void {
    this.metasArray.push(this.fb.control(valor, [Validators.required, Validators.maxLength(500)]));
    this.metasIds.push(id);
  }

  eliminarMeta(index: number): void {
    const id = this.metasIds[index];

    const quitarDelFormulario = () => {
      if (this.metasArray.length <= 1) {
        this.metasArray.at(0).setValue("");
        this.metasArray.at(0).markAsUntouched();
        this.metasIds[0] = null;
        return;
      }
      this.metasArray.removeAt(index);
      this.metasIds.splice(index, 1);
    };

    if (!id) {
      quitarDelFormulario();
      return;
    }

    Swal.fire({
      title: "¿Eliminar esta meta?",
      text: "La meta quedará inactiva.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.metasPlanCompetenciaService
        .inactivar(id)
        .pipe(
          catchError((err) => {
            Swal.fire("Error", this.extraerMensajeError(err, "No se pudo inactivar la meta."), "error");
            return of(null);
          }),
        )
        .subscribe((resp) => {
          if (resp === null) return;
          quitarDelFormulario();
        });
    });
  }

  private recalcularFechasPorAnio(anio: number | null): void {
    if (!anio) {
      this.planForm.patchValue({ fecha_inicio: null, fecha_fin: null }, { emitEvent: false });
      return;
    }

    const fechaInicio = `${anio}-01-01`;
    const fechaFin = `${anio}-12-31`;

    this.planForm.patchValue({ fecha_inicio: fechaInicio, fecha_fin: fechaFin }, { emitEvent: false });
  }

  cargarListado(): void {
    this.cargandoListado = true;

    this.planCompetenciaAnualService
      .getAll()
      .pipe(
        catchError((err) => {
          this.cargandoListado = false;
          Swal.fire(
            "Error",
            this.extraerMensajeError(err, "No se pudo obtener el listado de planes de competencia anual."),
            "error",
          );
          return of([]);
        }),
      )
      .subscribe((response: any) => {
        this.planes = this.normalizarLista(response);
        this.cargandoListado = false;
        this.aplicarFiltro();
      });
  }

  private normalizarLista(response: any): PlanCompetenciaAnual[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  private extraerMensajeError(err: any, mensajePorDefecto: string): string {
    return err?.error?.error || err?.error?.message || mensajePorDefecto;
  }

  aplicarFiltro(): void {
    const termino = String(this.filtro || "").toLowerCase().trim();

    let filtrados = this.planes.filter((p) => {
      const coincideTexto = !termino || (p.objetivo || "").toLowerCase().includes(termino);
      const coincideEstado = this.filtroEstado === null || Number(p.estado) === this.filtroEstado;
      return coincideTexto && coincideEstado;
    });

    filtrados = filtrados.sort((a, b) => {
      const estadoA = Number(a.estado) === 1 ? 0 : 1;
      const estadoB = Number(b.estado) === 1 ? 0 : 1;
      if (estadoA !== estadoB) return estadoA - estadoB;
      return (b.id || 0) - (a.id || 0);
    });

    this.planesFiltrados = filtrados;
    this.page = 1;
  }

  private abrirModal(modalRef: any): void {
    this.modalService.open(modalRef, { centered: true, size: "lg" });
  }

  private resetearMetasArray(valores: Array<{ texto: string; id: number | null }> = []): void {
    while (this.metasArray.length) {
      this.metasArray.removeAt(0);
    }
    this.metasIds = [];

    if (valores.length > 0) {
      valores.forEach((v) => this.agregarMeta(v.texto, v.id));
    } else {
      this.agregarMeta();
    }
  }

  abrirModalCrear(modalRef: any): void {
    this.modoEdicion = false;
    this.idEditando = null;

    this.planForm.reset({ objetivo: "", anio: null, fecha_inicio: null, fecha_fin: null });
    this.resetearMetasArray();
    this.abrirModal(modalRef);
  }

  abrirModalEditar(modalRef: any, plan: PlanCompetenciaAnual): void {
    this.modoEdicion = true;
    this.idEditando = plan.id ?? null;

    if (!this.idEditando) return;

    this.cargandoListado = true;

    forkJoin({
      plan: this.planCompetenciaAnualService.getById(this.idEditando).pipe(
        catchError((err) => {
          Swal.fire("Error", this.extraerMensajeError(err, "No se pudo obtener la información del plan."), "error");
          return of(null);
        }),
      ),
      todasLasMetas: this.metasPlanCompetenciaService.getAll().pipe(catchError(() => of([] as MetasPlanCompetencia[]))),
    }).subscribe(({ plan: data, todasLasMetas }) => {
      this.cargandoListado = false;

      if (!data) return;

      const registro = (data as any)?.data ?? data ?? plan;
      const anio = this.obtenerAnioDeFecha(registro.fecha_inicio);

      let metasExistentes: Array<{ id: number | null; texto: string }> = Array.isArray(
        registro.MetasPlanCompetenciaData,
      )
        ? registro.MetasPlanCompetenciaData
            .filter((meta: any) => Number(meta?.estado) === 1)
            .map((meta: any) => ({ id: meta?.id ?? null, texto: String(meta?.metas ?? "").trim() }))
            .filter((m: { texto: string }) => m.texto.length > 0)
        : [];

      if (metasExistentes.length === 0) {
        metasExistentes = (todasLasMetas || [])
          .filter((m) => Number(m.id_competencia) === Number(this.idEditando) && Number(m.estado) === 1)
          .map((m) => ({ id: m.id ?? null, texto: String(m.metas ?? "").trim() }))
          .filter((m) => m.texto.length > 0);
      }

      this.planForm.reset({
        objetivo: registro.objetivo,
        anio: anio,
        fecha_inicio: this.formatearFechaInput(registro.fecha_inicio),
        fecha_fin: this.formatearFechaInput(registro.fecha_fin),
      });

      this.resetearMetasArray(metasExistentes);
      this.abrirModal(modalRef);
    });
  }

  private obtenerAnioDeFecha(fecha: string): number | null {
    if (!fecha) return null;
    const anio = Number(fecha.substring(0, 4));
    return isNaN(anio) ? null : anio;
  }

  private formatearFechaInput(fecha: string): string {
    if (!fecha) return "";
    return fecha.substring(0, 10);
  }

  guardar(modal: any): void {
    if (this.planForm.invalid) {
      this.planForm.markAllAsTouched();
      Swal.fire(
        "Campos obligatorios",
        "Completa el objetivo, selecciona el año y registra al menos una meta del plan.",
        "warning",
      );
      return;
    }

    const raw = this.planForm.getRawValue();

    const metasFormulario: Array<{ id: number | null; texto: string }> = (raw.metas as string[]).map(
      (texto, i) => ({ id: this.metasIds[i] ?? null, texto: String(texto || "").trim() }),
    );

    const metasValidas = metasFormulario.filter((m) => m.texto.length > 0);

    if (metasValidas.length === 0) {
      Swal.fire("Campos obligatorios", "Registra al menos una meta para el plan.", "warning");
      return;
    }

    const payload: PlanCompetenciaAnualPayload = {
      objetivo: raw.objetivo,
      fecha_inicio: raw.fecha_inicio,
      fecha_fin: raw.fecha_fin,
      metas: metasValidas.map((m) => m.texto),
    };

    this.guardando = true;

    const peticion =
      this.modoEdicion && this.idEditando
        ? this.planCompetenciaAnualService.update(this.idEditando, payload)
        : this.planCompetenciaAnualService.create({ ...payload, estado: 1 });

    peticion
      .pipe(
        catchError((err) => {
          this.guardando = false;
          Swal.fire(
            "Error",
            this.extraerMensajeError(err, "Ocurrió un error al guardar el plan de competencia anual."),
            "error",
          );
          return of(null);
        }),
      )
      .subscribe((resp: any) => {
        if (resp === null) return;

        const idPlan: number | undefined = (this.modoEdicion && this.idEditando) || resp?.data?.id || resp?.id;

        if (!idPlan) {
          this.guardando = false;
          modal.close();
          Swal.fire(
            "Advertencia",
            "El plan se guardó, pero no fue posible determinar su id para guardar las metas.",
            "warning",
          );
          this.cargarListado();
          return;
        }
        
        const metasNuevas = metasValidas.filter((m) => !m.id).map((m) => m.texto);
        const metasExistentes = metasValidas.filter(
          (m): m is { id: number; texto: string } => !!m.id,
        );

        this.guardarMetas(idPlan, metasNuevas, metasExistentes, modal);
      });
  }

  private guardarMetas(
    idPlan: number,
    metasNuevas: string[],
    metasExistentes: Array<{ id: number; texto: string }>,
    modal: any,
  ): void {
    const operaciones = [
      ...metasNuevas.map((texto) =>
        this.metasPlanCompetenciaService
          .create({ id_competencia: idPlan, metas: texto, estado: 1 })
          .pipe(catchError(() => of(null))),
      ),
      ...metasExistentes.map((meta) =>
        this.metasPlanCompetenciaService
          .update(meta.id, { metas: meta.texto })
          .pipe(catchError(() => of(null))),
      ),
    ];

    if (operaciones.length === 0) {
      this.finalizarGuardado(modal);
      return;
    }

    forkJoin(operaciones)
      .pipe(catchError(() => of(null)))
      .subscribe(() => this.finalizarGuardado(modal));
  }

  private finalizarGuardado(modal: any): void {
    this.guardando = false;
    modal.close();

    Swal.fire(
      "Éxito",
      this.modoEdicion
        ? "Plan de competencia anual actualizado correctamente."
        : "Plan de competencia anual creado correctamente.",
      "success",
    );

    this.cargarListado();
  }

  cambiarEstado(plan: PlanCompetenciaAnual, estado: number): void {
    if (!plan.id) return;

    const accion = estado === 1 ? "activar" : "inactivar";

    Swal.fire({
      title: `¿Deseas ${accion} este plan de competencia anual?`,
      text: plan.objetivo,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const peticion =
        estado === 1
          ? this.planCompetenciaAnualService.activar(plan.id as number)
          : this.planCompetenciaAnualService.inactivar(plan.id as number);

      peticion
        .pipe(
          catchError((err) => {
            Swal.fire(
              "Error",
              this.extraerMensajeError(err, `No se pudo ${accion} el plan de competencia anual.`),
              "error",
            );
            return of(null);
          }),
        )
        .subscribe((resp: any) => {
          if (resp === null) return;
          Swal.fire("Éxito", `Plan ${accion === "activar" ? "activado" : "inactivado"} correctamente.`, "success");
          this.cargarListado();
        });
    });
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  // ===================== CRONOGRAMA ANUAL (Excel) =====================

  private readonly MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
  private readonly COLOR_ENCABEZADO = "FF1F6E43";
  private readonly COLOR_PROGRAMADO = "FFFFF200";
  private readonly COLOR_EJECUTADO = "FF92D050";

  exportarCronogramaExcel(plan: PlanCompetenciaAnual): void {
    const anio = this.obtenerAnioDeFecha(plan.fecha_inicio);

    if (!anio) {
      Swal.fire("Error", "No se pudo determinar el año del plan.", "error");
      return;
    }

    this.generandoExcel = true;

    this.planCompetenciaAnualService
      .getCronograma(anio)
      .pipe(
        catchError((err) => {
          this.generandoExcel = false;
          Swal.fire("Error", this.extraerMensajeError(err, "No se pudo generar el cronograma anual."), "error");
          return of(null);
        }),
      )
      .subscribe(async (data: CronogramaAnualResponse | null) => {
        if (!data) return;

        try {
          await this.construirExcelCronograma(anio, plan.objetivo, data);
        } catch (error) {
          Swal.fire("Error", "Ocurrió un error al generar el archivo Excel.", "error");
        } finally {
          this.generandoExcel = false;
        }
      });
  }

  private async construirExcelCronograma(
    anio: number,
    objetivo: string,
    data: CronogramaAnualResponse,
  ): Promise<void> {
    const nombreEmpresa = await this.obtenerNombreEmpresa();
    const coberturaPersonal = await this.calcularCoberturaPersonal(data); // 👈 NUEVO
    const coberturaPorCargo = await this.calcularCoberturaPorCargo(data); // 👈 NUEVO

    const workbook = new ExcelJS.Workbook();

    this.construirHojaCronograma(workbook, anio, objetivo, nombreEmpresa, data, coberturaPersonal);
    await this.construirHojaIndicadores(workbook, anio, nombreEmpresa, data, coberturaPersonal, coberturaPorCargo);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `cronograma_formacion_seguridad_vial_${anio}.xlsx`);
  }

  // ===================== INDICADOR 12 — CPF PESV (Cobertura de personal) =====================

  private obtenerColaboradoresActivos(): Promise<any[]> {
    return new Promise((resolve) => {
      this.propietarioService
        .getPlanAccion()
        .pipe(catchError(() => of([])))
        .subscribe((colaboradores: any[]) => resolve(colaboradores || []));
    });
  }

  /**
   * Indicador 12 — CPF PESV: CFSV(t) / CT(t) * 100
   * CT(t): total de colaboradores activos de la organización.
   * CFSV(t): colaboradores (cédulas únicas) que asistieron a al menos una capacitación en el año.
   */
  private async calcularCoberturaPersonal(data: CronogramaAnualResponse): Promise<CoberturaPersonal> {
    const colaboradores = await this.obtenerColaboradoresActivos();

    const cedulasTotales = new Set(colaboradores.map((c: any) => String(c.cedula)));
    const totalColaboradores = cedulasTotales.size;

    const cedulasCapacitadas = new Set<string>();
    (data.programaciones || []).forEach((p) => {
      (p.asistentes || []).forEach((a) => {
        if (Number(a.asistio ?? 0) === 1 && a.cedula != null) {
          cedulasCapacitadas.add(String(a.cedula));
        }
      });
    });

    const capacitados = cedulasCapacitadas.size;
    const porcentaje =
      totalColaboradores > 0 ? Math.round((capacitados / totalColaboradores) * 100 * 100) / 100 : 0;

    return { capacitados, totalColaboradores, porcentaje };
  }

  /**
   * Desglose complementario (no oficial) por cargo: útil para gestión interna,
   * no reemplaza el indicador CPF PESV que se calcula sobre el total de colaboradores.
   */
  private async calcularCoberturaPorCargo(data: CronogramaAnualResponse): Promise<CoberturaPorCargo[]> {
    const colaboradores = await this.obtenerColaboradoresActivos();

    // Mapa cédula -> nombre de cargo (viene embebido como cargoData en getPlanAccion)
    const cargoPorCedula = new Map<string, string>();
    colaboradores.forEach((c: any) => {
      const nombreCargo = c?.cargoData?.cargos ?? "SIN CARGO";
      cargoPorCedula.set(String(c.cedula), nombreCargo);
    });

    // Total de colaboradores por cargo
    const totalesPorCargo = new Map<string, number>();
    colaboradores.forEach((c: any) => {
      const nombreCargo = c?.cargoData?.cargos ?? "SIN CARGO";
      totalesPorCargo.set(nombreCargo, (totalesPorCargo.get(nombreCargo) ?? 0) + 1);
    });

    // Cédulas capacitadas (únicas, igual que en el indicador oficial)
    const cedulasCapacitadas = new Set<string>();
    (data.programaciones || []).forEach((p) => {
      (p.asistentes || []).forEach((a) => {
        if (Number(a.asistio ?? 0) === 1 && a.cedula != null) {
          cedulasCapacitadas.add(String(a.cedula));
        }
      });
    });

    // Capacitados por cargo
    const capacitadosPorCargo = new Map<string, number>();
    cedulasCapacitadas.forEach((cedula) => {
      const nombreCargo = cargoPorCedula.get(cedula) ?? "SIN CARGO";
      capacitadosPorCargo.set(nombreCargo, (capacitadosPorCargo.get(nombreCargo) ?? 0) + 1);
    });

    const resultado: CoberturaPorCargo[] = [];
    totalesPorCargo.forEach((totalColaboradores, cargo) => {
      const capacitados = capacitadosPorCargo.get(cargo) ?? 0;
      const porcentaje = totalColaboradores > 0 ? Math.round((capacitados / totalColaboradores) * 100 * 100) / 100 : 0;
      resultado.push({ cargo, totalColaboradores, capacitados, porcentaje });
    });

    return resultado.sort((a, b) => a.cargo.localeCompare(b.cargo));
  }

  private construirHojaCronograma(
    workbook: ExcelJS.Workbook,
    anio: number,
    objetivo: string,
    nombreEmpresa: string,
    data: CronogramaAnualResponse,
    coberturaPersonal: CoberturaPersonal, // 👈 NUEVO parámetro
  ): void {
    const hoja = workbook.addWorksheet(String(anio));
    const totalColumnas = 4 + this.MESES.length * 2;
    const ultimaColumna = this.columnaLetra(totalColumnas);
    console.log("inicio de construcción de hoja de cronograma");
    console.log("nombreEmpresa:", nombreEmpresa);
    console.log("anio:", anio);
    console.log("objetivo:", objetivo);
    console.log("coberturaPersonal:", coberturaPersonal);
    hoja.mergeCells("A1:D3");
    hoja.getCell("A1").value = nombreEmpresa;
    hoja.getCell("A1").font = { bold: true, size: 11 };
    hoja.getCell("A1").alignment = { vertical: "middle", horizontal: "center", wrapText: true };

    hoja.mergeCells(`E1:${ultimaColumna}2`);
    hoja.getCell("E1").value = "CRONOGRAMA ANUAL DE FORMACIÓN EN SEGURIDAD VIAL";
    hoja.getCell("E1").font = { bold: true, size: 13 };
    hoja.getCell("E1").alignment = { vertical: "middle", horizontal: "center" };

    hoja.mergeCells(`E3:${ultimaColumna}3`);
    hoja.getCell("E3").value = "PLAN ESTRATÉGICO DE SEGURIDAD VIAL";
    hoja.getCell("E3").font = { bold: true };
    hoja.getCell("E3").alignment = { vertical: "middle", horizontal: "center" };

    let fila = 4;

    fila = this.escribirFilaTituloVerde(hoja, fila, ultimaColumna, "NOMBRE: CRONOGRAMA ANUAL DE FORMACIÓN EN SEGURIDAD VIAL");
    fila = this.escribirFilaTituloVerde(hoja, fila, ultimaColumna, "OBJETIVO");

    hoja.mergeCells(`A${fila}:${ultimaColumna}${fila}`);
    hoja.getCell(`A${fila}`).value = objetivo || "";
    hoja.getCell(`A${fila}`).alignment = { wrapText: true, vertical: "middle" };
    hoja.getRow(fila).height = 30;
    fila += 1;

    const escribirBloqueIndicadorSimple = (filaInicio: number, titulo: string, valor: string): number => {
      let f = this.escribirFilaTituloVerde(hoja, filaInicio, ultimaColumna, titulo);
      hoja.mergeCells(`A${f}:${ultimaColumna}${f}`);
      hoja.getCell(`A${f}`).value = valor;
      hoja.getCell(`A${f}`).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      hoja.getRow(f).height = 20;
      return f + 1;
    };

    const indicadores = this.calcularIndicadores(data);

    // Indicador de cumplimiento de programas (existente, renombrado para no confundir con CPF PESV)
    fila = escribirBloqueIndicadorSimple(fila, "NOMBRE DEL INDICADOR DE CUMPLIMIENTO", "EFICACIA DE LA CAPACITACIÓN");
    fila = escribirBloqueIndicadorSimple(
      fila,
      "1. FORMULA DEL INDICADOR DE CUMPLIMIENTO",
      `No. Temas ejecutados / No. Temas programados * 100 = ${indicadores.eficacia.ejecutadas}/${indicadores.eficacia.programadas} = ${indicadores.eficacia.porcentaje}%`,
    );

    // Indicador 12 — CPF PESV: cobertura real de personal capacitado
    fila = escribirBloqueIndicadorSimple(fila, "NOMBRE DEL INDICADOR (CPF PESV)", "COBERTURA DEL PLAN DE FORMACIÓN EN SEGURIDAD VIAL");
    fila = escribirBloqueIndicadorSimple(
      fila,
      "2. FORMULA DEL INDICADOR CPF PESV",
      `Colaboradores capacitados / Total colaboradores * 100 = ${coberturaPersonal.capacitados}/${coberturaPersonal.totalColaboradores} = ${coberturaPersonal.porcentaje}%`,
    );

    fila = this.escribirFilaTituloVerde(hoja, fila, ultimaColumna, "METAS");

    const metas = this.normalizarMetasCronograma(data.metas);
    const metasAUsar = metas.length > 0 ? metas : ["Sin metas registradas para este plan."];
    const colEtiquetaMeta = this.columnaLetra(4);
    const colValorMetaInicio = this.columnaLetra(5);

    metasAUsar.forEach((textoMeta, idx) => {
      hoja.mergeCells(`A${fila}:${colEtiquetaMeta}${fila}`);
      const celdaEtiqueta = hoja.getCell(`A${fila}`);
      celdaEtiqueta.value = `META ${idx + 1}`;
      celdaEtiqueta.font = { bold: true };
      celdaEtiqueta.alignment = { horizontal: "center", vertical: "middle" };
      celdaEtiqueta.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
      celdaEtiqueta.border = this.bordeCompleto();

      hoja.mergeCells(`${colValorMetaInicio}${fila}:${ultimaColumna}${fila}`);
      const celdaValor = hoja.getCell(`${colValorMetaInicio}${fila}`);
      celdaValor.value = textoMeta;
      celdaValor.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
      celdaValor.border = this.bordeCompleto();

      fila++;
    });

    fila = this.escribirFilaTituloVerde(hoja, fila, ultimaColumna, "ALCANCE");
    hoja.mergeCells(`A${fila}:${ultimaColumna}${fila}`);
    hoja.getCell(`A${fila}`).value = `Aplica para toda la empresa ${nombreEmpresa}`;
    hoja.getCell(`A${fila}`).alignment = { horizontal: "center", vertical: "middle" };
    fila++;

    const tercio = Math.round(totalColumnas / 3);
    const colTercio1 = this.columnaLetra(tercio);
    const colTercio1Mas1 = this.columnaLetra(tercio + 1);
    const colTercio2 = this.columnaLetra(tercio * 2);
    const colTercio2Mas1 = this.columnaLetra(tercio * 2 + 1);

    const escribirFilaTresColumnas = (f: number, a: string, b: string, c: string, esEncabezado: boolean) => {
      hoja.mergeCells(`A${f}:${colTercio1}${f}`);
      hoja.getCell(`A${f}`).value = a;
      hoja.mergeCells(`${colTercio1Mas1}${f}:${colTercio2}${f}`);
      hoja.getCell(`${colTercio1Mas1}${f}`).value = b;
      hoja.mergeCells(`${colTercio2Mas1}${f}:${ultimaColumna}${f}`);
      hoja.getCell(`${colTercio2Mas1}${f}`).value = c;

      [hoja.getCell(`A${f}`), hoja.getCell(`${colTercio1Mas1}${f}`), hoja.getCell(`${colTercio2Mas1}${f}`)].forEach((cel) => {
        cel.alignment = { horizontal: "center", vertical: "middle" };
        if (esEncabezado) {
          cel.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.COLOR_ENCABEZADO } };
        }
      });
    };

    escribirFilaTresColumnas(fila, "ACCION", "PRIORIDAD", "RESPONSABILIDAD", true);
    fila++;
    escribirFilaTresColumnas(fila, "Planificación", "1", "Inspección Seguridad Vial y HSE", false);
    fila++;

    const filaMes = fila;
    const filaPE = fila + 1;

    hoja.mergeCells(`A${filaMes}:A${filaPE}`);
    hoja.getCell(`A${filaMes}`).value = "N°";

    hoja.mergeCells(`B${filaMes}:B${filaPE}`);
    hoja.getCell(`B${filaMes}`).value = "ACTIVIDADES";

    hoja.mergeCells(`C${filaMes}:C${filaPE}`);
    hoja.getCell(`C${filaMes}`).value = "DIRIGIDO A";

    hoja.mergeCells(`D${filaMes}:D${filaPE}`);
    hoja.getCell(`D${filaMes}`).value = "RESPONSABLE";

    let colIndex = 5;
    this.MESES.forEach((mes) => {
      const colP = this.columnaLetra(colIndex);
      const colE = this.columnaLetra(colIndex + 1);

      hoja.mergeCells(`${colP}${filaMes}:${colE}${filaMes}`);
      hoja.getCell(`${colP}${filaMes}`).value = mes;
      hoja.getCell(`${colP}${filaPE}`).value = "P";
      hoja.getCell(`${colE}${filaPE}`).value = "E";

      colIndex += 2;
    });

    [filaMes, filaPE].forEach((f) => {
      hoja.getRow(f).eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.COLOR_ENCABEZADO } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = this.bordeCompleto();
      });
    });

    fila = filaPE + 1;

    const COLOR_PROGRAMA = "FFD9E1F2";
    let totalFilasDeDatos = 0;

    data.programaciones.forEach((programacion) => {
      const nombrePrograma = programacion.capacitacion?.nombre || "PROGRAMA SIN NOMBRE";
      const indicadorPrograma = indicadores.porCapacitacion.get(programacion.id);
      const textoIndicador = indicadorPrograma
        ? ` — Cumplimiento: ${indicadorPrograma.ejecutadas}/${indicadorPrograma.programadas} (${indicadorPrograma.porcentaje}%)`
        : "";

      hoja.mergeCells(`A${fila}:${ultimaColumna}${fila}`);
      const celdaPrograma = hoja.getCell(`A${fila}`);
      celdaPrograma.value = `${nombrePrograma}${textoIndicador}`;
      celdaPrograma.font = { bold: true };
      celdaPrograma.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_PROGRAMA } };
      celdaPrograma.alignment = { horizontal: "left", vertical: "middle" };
      celdaPrograma.border = this.bordeCompleto();
      fila++;

      const filasPrograma: Array<{
        idActividad: number;
        actividad: string;
        dirigido: string;
        responsable: string;
        mesProgramado: number | null;
        mesesEjecutados: number[];
      }> = [];

      (programacion.actividades || []).forEach((actividad) => {
        (actividad.actividadre || []).forEach((re) => {
          const mesesEjecutados = (actividad.ejecuciones || [])
            .map((ej: any) => this.obtenerMesDeFecha(ej.fechaCreacion))
            .filter((m): m is number => m !== null);

          filasPrograma.push({
            idActividad: actividad.id,
            actividad: re.actividad,
            dirigido: actividad.dirigido,
            responsable: actividad.responsable,
            mesProgramado: actividad.mes_programado,
            mesesEjecutados,
          });
        });
      });

      if (filasPrograma.length === 0) {
        hoja.mergeCells(`A${fila}:${ultimaColumna}${fila}`);
        hoja.getCell(`A${fila}`).value = "Sin actividades registradas para este programa.";
        hoja.getCell(`A${fila}`).alignment = { horizontal: "center" };
        hoja.getCell(`A${fila}`).font = { italic: true };
        fila++;
      } else {
        let inicioBloque = fila;
        let idActividadAnterior: number | null = null;

        filasPrograma.forEach((item) => {
          const filaExcel = hoja.getRow(fila);

          totalFilasDeDatos++;
          filaExcel.getCell(1).value = totalFilasDeDatos;
          filaExcel.getCell(2).value = item.actividad;
          filaExcel.getCell(3).value = item.dirigido;
          filaExcel.getCell(4).value = item.responsable;

          let col = 5;
          for (let mes = 1; mes <= 12; mes++) {
            const celdaP = filaExcel.getCell(col);
            const celdaE = filaExcel.getCell(col + 1);

            if (item.mesProgramado === mes) {
              celdaP.value = 1;
              celdaP.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.COLOR_PROGRAMADO } };
            }

            if (item.mesesEjecutados.includes(mes)) {
              celdaE.value = 1;
              celdaE.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.COLOR_EJECUTADO } };
            }

            col += 2;
          }

          filaExcel.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = this.bordeCompleto();
            cell.alignment = { horizontal: "center", vertical: "middle" };
          });
          filaExcel.getCell(2).alignment = { horizontal: "left", vertical: "middle", wrapText: true };

          if (idActividadAnterior !== null && item.idActividad !== idActividadAnterior && fila - 1 > inicioBloque) {
            hoja.mergeCells(`C${inicioBloque}:C${fila - 1}`);
            hoja.mergeCells(`D${inicioBloque}:D${fila - 1}`);
          }
          if (idActividadAnterior === null || item.idActividad !== idActividadAnterior) {
            inicioBloque = fila;
          }
          idActividadAnterior = item.idActividad;

          fila++;
        });

        if (fila - 1 > inicioBloque) {
          hoja.mergeCells(`C${inicioBloque}:C${fila - 1}`);
          hoja.mergeCells(`D${inicioBloque}:D${fila - 1}`);
        }
      }
    });

    if (data.programaciones.length === 0 || totalFilasDeDatos === 0) {
      hoja.mergeCells(`A${fila}:${ultimaColumna}${fila}`);
      hoja.getCell(`A${fila}`).value = "No hay capacitaciones programadas para este año.";
      hoja.getCell(`A${fila}`).alignment = { horizontal: "center" };
      fila++;
    }

    hoja.getColumn(1).width = 5;
    hoja.getColumn(2).width = 42;
    hoja.getColumn(3).width = 20;
    hoja.getColumn(4).width = 24;
    for (let c = 5; c <= totalColumnas; c++) {
      hoja.getColumn(c).width = 6;
    }
  }

  private generarImagenGraficoBarras(data: CronogramaAnualResponse): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      canvas.width = 900;
      canvas.height = 380;
      canvas.style.position = "fixed";
      canvas.style.left = "-9999px";
      document.body.appendChild(canvas);

      const labels = data.resumenMensual.map((r) => r.nombre.toUpperCase());
      const programadas = data.resumenMensual.map((r) => r.programadas);
      const ejecutadas = data.resumenMensual.map((r) => r.ejecutadas);

      let chart: Chart | null = null;

      try {
        chart = new Chart(canvas, {
          type: "bar",
          data: {
            labels,
            datasets: [
              { label: "ACTIVIDADES PROGRAMADAS", data: programadas, backgroundColor: "#4472C4" },
              { label: "ACTIVIDADES EJECUTADAS", data: ejecutadas, backgroundColor: "#C00000" },
            ],
          },
          options: {
            responsive: false,
            animation: false,
            plugins: {
              legend: { position: "bottom" },
              title: { display: true, text: "Indicador", font: { size: 16, weight: "bold" } },
            },
            scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
          },
        });
      } catch (err) {
        document.body.removeChild(canvas);
        reject(err);
        return;
      }

      requestAnimationFrame(() => {
        try {
          const base64 = canvas.toDataURL("image/png").split(",")[1];
          chart?.destroy();
          document.body.removeChild(canvas);
          resolve(base64);
        } catch (err) {
          chart?.destroy();
          document.body.removeChild(canvas);
          reject(err);
        }
      });
    });
  }

  private async construirHojaIndicadores(
    workbook: ExcelJS.Workbook,
    anio: number,
    nombreEmpresa: string,
    data: CronogramaAnualResponse,
    coberturaPersonal: CoberturaPersonal, // 👈 NUEVO parámetro
    coberturaPorCargo: CoberturaPorCargo[], // 👈 NUEVO parámetro
  ): Promise<void> {
    const hoja = workbook.addWorksheet(`Indicadores ${anio}`);

    hoja.mergeCells("A1:H1");
    hoja.getCell("A1").value = nombreEmpresa;
    hoja.getCell("A1").font = { bold: true };

    hoja.mergeCells("A2:H2");
    hoja.getCell("A2").value = "CRONOGRAMA ANUAL DE FORMACIÓN EN SEGURIDAD VIAL - INDICADOR";
    hoja.getCell("A2").font = { bold: true, size: 12 };
    hoja.getCell("A2").alignment = { horizontal: "center" };

    const indicadores = this.calcularIndicadores(data);
    hoja.mergeCells("A3:H3");
    hoja.getCell("A3").value =
      `Eficacia: ${indicadores.eficacia.ejecutadas}/${indicadores.eficacia.programadas} (${indicadores.eficacia.porcentaje}%)` +
      `   |   CPF PESV: ${coberturaPersonal.capacitados}/${coberturaPersonal.totalColaboradores} (${coberturaPersonal.porcentaje}%)`;
    hoja.getCell("A3").font = { italic: true };
    hoja.getCell("A3").alignment = { horizontal: "center" };

    const imagenBase64 = await this.generarImagenGraficoBarras(data);
    const imageId = workbook.addImage({ base64: imagenBase64, extension: "png" });

    const filaInicioGrafico = 4;
    hoja.addImage(imageId, {
      tl: { col: 0, row: filaInicioGrafico - 1 },
      ext: { width: 900, height: 380 },
    });

    let fila = filaInicioGrafico + Math.ceil(380 / 20) + 2;

    // ===================== TABLA: COBERTURA DE PERSONAL POR CARGO =====================
    // (Valor agregado — desglose complementario al indicador CPF PESV oficial)

    hoja.mergeCells(`A${fila}:D${fila}`);
    hoja.getCell(`A${fila}`).value = "COBERTURA DE FORMACIÓN POR CARGO (información complementaria)";
    hoja.getCell(`A${fila}`).font = { bold: true, color: { argb: "FFFFFFFF" } };
    hoja.getCell(`A${fila}`).fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.COLOR_ENCABEZADO } };
    hoja.getCell(`A${fila}`).alignment = { horizontal: "center", vertical: "middle" };
    fila++;

    hoja.getCell(`A${fila}`).value = "Cargo";
    hoja.getCell(`B${fila}`).value = "Total colaboradores";
    hoja.getCell(`C${fila}`).value = "Capacitados";
    hoja.getCell(`D${fila}`).value = "% Cobertura";
    ["A", "B", "C", "D"].forEach((col) => {
      const cel = hoja.getCell(`${col}${fila}`);
      cel.font = { bold: true };
      cel.border = this.bordeCompleto();
      cel.alignment = { horizontal: "center", vertical: "middle" };
    });
    fila++;

    coberturaPorCargo.forEach((item) => {
      hoja.getCell(`A${fila}`).value = item.cargo;
      hoja.getCell(`B${fila}`).value = item.totalColaboradores;
      hoja.getCell(`C${fila}`).value = item.capacitados;
      hoja.getCell(`D${fila}`).value = `${item.porcentaje}%`;
      ["A", "B", "C", "D"].forEach((col) => {
        const cel = hoja.getCell(`${col}${fila}`);
        cel.border = this.bordeCompleto();
        cel.alignment = { horizontal: col === "A" ? "left" : "center", vertical: "middle" };
      });
      fila++;
    });

    fila += 2;

    // ===================== TRIMESTRES =====================

    const trimestres = [
      "Primer Trimestre (ene-feb-mar)",
      "Segundo Trimestre (abr-may-jun)",
      "Tercer Trimestre (jul-ago-sep)",
      "Cuarto Trimestre (oct-nov-dic)",
    ];

    trimestres.forEach((titulo) => {
      hoja.mergeCells(`A${fila}:C${fila}`);
      hoja.getCell(`A${fila}`).value = titulo;
      hoja.getCell(`A${fila}`).font = { bold: true };
      hoja.getCell(`A${fila}`).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFBDD7EE" } };
      hoja.getCell(`A${fila}`).alignment = { horizontal: "center" };
      fila++;

      hoja.getCell(`A${fila}`).value = "Análisis de tendencias e indicadores";
      hoja.getCell(`B${fila}`).value = "Descripción";
      hoja.getCell(`C${fila}`).value = "Responsable";
      hoja.getRow(fila).eachCell((cell) => {
        cell.font = { bold: true };
        cell.border = this.bordeCompleto();
      });
      fila++;

      for (let i = 0; i < 3; i++) {
        ["A", "B", "C"].forEach((col) => {
          hoja.getCell(`${col}${fila}`).border = this.bordeCompleto();
        });
        fila++;
      }

      fila++;
    });

    hoja.getColumn(1).width = 22;
    hoja.getColumn(2).width = 20;
    hoja.getColumn(3).width = 18;
    hoja.getColumn(4).width = 14;
  }

  private columnaLetra(indice: number): string {
    let letra = "";
    let n = indice;
    while (n > 0) {
      const resto = (n - 1) % 26;
      letra = String.fromCharCode(65 + resto) + letra;
      n = Math.floor((n - 1) / 26);
    }
    return letra;
  }

  private bordeCompleto(): Partial<ExcelJS.Borders> {
    return {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  }

  private normalizarMetasCronograma(
    metas?: Array<string | { metas?: string; estado?: number; [key: string]: any }>,
  ): string[] {
    if (!Array.isArray(metas)) return [];

    return metas
      .filter((meta) => typeof meta === "string" || Number((meta as any)?.estado ?? 1) === 1)
      .map((meta) => (typeof meta === "string" ? meta.trim() : String((meta as any)?.metas ?? "").trim()))
      .filter((meta) => meta.length > 0);
  }

  private calcularIndicadores(data: CronogramaAnualResponse): {
    eficacia: { programadas: number; ejecutadas: number; porcentaje: number };
    cobertura: { programadas: number; ejecutadas: number; porcentaje: number };
    porCapacitacion: Map<number, { programadas: number; ejecutadas: number; porcentaje: number }>;
  } {
    const porCapacitacion = new Map<number, { programadas: number; ejecutadas: number; porcentaje: number }>();

    let totalProgramadasGlobal = 0;
    let totalEjecutadasGlobal = 0;

    let capacitacionesConProgramacion = 0;
    let capacitacionesCumplidas = 0;

    (data.programaciones || []).forEach((programacion) => {
      let programadasCap = 0;
      let ejecutadasCap = 0;

      (programacion.actividades || []).forEach((actividad: any) => {
        const temas = actividad.actividadre || [];
        const totalTemas = temas.length > 0 ? temas.length : 1;

        if (actividad.mes_programado) {
          programadasCap += totalTemas;
        }

        const tieneEjecucion = (actividad.ejecuciones || []).length > 0;
        if (tieneEjecucion) {
          ejecutadasCap += totalTemas;
        }
      });

      totalProgramadasGlobal += programadasCap;
      totalEjecutadasGlobal += ejecutadasCap;

      const porcentajeCap = programadasCap > 0 ? Math.round((ejecutadasCap / programadasCap) * 100 * 100) / 100 : 0;

      porCapacitacion.set(programacion.id, {
        programadas: programadasCap,
        ejecutadas: ejecutadasCap,
        porcentaje: porcentajeCap,
      });

      if (programadasCap > 0) {
        capacitacionesConProgramacion += 1;
        if (ejecutadasCap >= programadasCap) {
          capacitacionesCumplidas += 1;
        }
      }
    });

    const eficaciaPorcentaje =
      totalProgramadasGlobal > 0 ? Math.round((totalEjecutadasGlobal / totalProgramadasGlobal) * 100 * 100) / 100 : 0;

    const coberturaPorcentaje =
      capacitacionesConProgramacion > 0
        ? Math.round((capacitacionesCumplidas / capacitacionesConProgramacion) * 100 * 100) / 100
        : 0;

    return {
      eficacia: { programadas: totalProgramadasGlobal, ejecutadas: totalEjecutadasGlobal, porcentaje: eficaciaPorcentaje },
      cobertura: {
        programadas: capacitacionesConProgramacion,
        ejecutadas: capacitacionesCumplidas,
        porcentaje: coberturaPorcentaje,
      },
      porCapacitacion,
    };
  }

  private escribirFilaTituloVerde(hoja: ExcelJS.Worksheet, fila: number, ultimaColumna: string, texto: string): number {
    hoja.mergeCells(`A${fila}:${ultimaColumna}${fila}`);
    const celda = hoja.getCell(`A${fila}`);
    celda.value = texto;
    celda.font = { bold: true, color: { argb: "FFFFFFFF" } };
    celda.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.COLOR_ENCABEZADO } };
    celda.alignment = { horizontal: "center", vertical: "middle" };
    return fila + 1;
  }

  private async obtenerNombreEmpresa(): Promise<string> {
    try {
      const admin: any = await this.administracionService.obtenerTodas().toPromise?.();
      const registro = Array.isArray(admin) ? admin[0] : admin?.data?.[0] ?? admin;
      return registro?.nombre || registro?.razon_social || "EMPRESA";
    } catch {
      return "EMPRESA";
    }
  }

  private obtenerMesDeFecha(fecha: string | null | undefined): number | null {
    if (!fecha) return null;
    const parteFecha = String(fecha).trim().split(/[ T]/)[0];
    const mes = Number(parteFecha.split("-")[1]);
    return isNaN(mes) || mes < 1 || mes > 12 ? null : mes;
  }
}